import { useEffect, useState } from "react";
import { Box, CircularProgress, Grid, Stack } from "@mui/material";
import ApiService from "../../../utils/fetch";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from 'powerbi-client';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from "react-redux";
import { setActiveDataElement } from "../../../redux/userSlice";


// Styled pill container and pill label (reuse style from ForecastTracker)
const PillContainer = styled('div')(({ theme }) => ({
    position: 'relative',
    display: 'inline-flex',
    borderRadius: 20,
    background: '#f5f5f5',
    height: 32, // reduced height
    minWidth: 550, // slightly reduced width
    alignItems: 'center',
    boxShadow: '0 0 0 1px #ddd',
}));

const PillHighlight = styled('div')(({ selected }) => ({
    position: 'absolute',
    top: 2,
    left: selected * (100 / 2) + '%',
    width: `calc(${100 / 2}% - 4px)`,
    height: 'calc(100% - 4px)',
    background: '#222',
    borderRadius: 16,
    transition: 'left 0.25s cubic-bezier(.4,0,.2,1)',
    zIndex: 1,
}));

const PillLabel = styled('div')(({ active }) => ({
    position: 'relative',
    zIndex: 2,
    flex: 1,
    textAlign: 'center',
    fontSize: '0.95rem',
    fontWeight: active ? 600 : 400,
    color: active ? '#fff' : '#222',
    cursor: 'pointer',
    minWidth: 140, // same width for all pills
    padding: '0 12px',
    userSelect: 'none',
    transition: 'color 0.2s',
    lineHeight: '28px',
}));


const PowerBIReports = () => {
    const dispatch = useDispatch();
    const [selectedChartTab, setSelectedChartTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [embedConfig, setEmbedConfig] = useState(null);

    const groupId = process.env.REACT_APP_POWER_BI_GROUP_ID;
    const reportId = process.env.REACT_APP_POWER_BI_REPORT_ID;
    const userId = useSelector((state) => state.user.userData.id);
    const allCustomerList = useSelector((state) => state.entity.allCustomerList);

    useEffect(() => {
        dispatch(setActiveDataElement("Bottler Concentrate Forecast"));
    }, [dispatch]);

    const chartTabs = [
        {
            label: "Forecast Tracker",
            pageName: process.env.REACT_APP_FORECAST_TRACKER_PAGE,
            filters: [
                {
                    $schema: "http://powerbi.com/product/schema#basic",
                    target: { table: "vw_forecast_tracker_user_summary", column: "user_details_id" },
                    operator: "Equals",
                    values: [userId],
                    filterType: models.FilterType.BasicFilter
                }
            ]
        },
        {
            label: "Performance History",
            pageName: process.env.REACT_APP_PERFORMANCE_HISTORY_PAGE,
            filters: [
                                                {
                    $schema: "http://powerbi.com/product/schema#basic",
                    target: { table: "vw_forecast_ontime_variance_summary", column: "customer_id" },
                    operator: "Equals",
                    values: allCustomerList,
                    filterType: models.FilterType.BasicFilter
                },
                {
                    $schema: "http://powerbi.com/product/schema#basic",
                    target: { table: "vw_forecast_tracker_user_summary", column: "customer_id" },
                    operator: "Equals",
                    values: allCustomerList,
                    filterType: models.FilterType.BasicFilter
                }
            ]
        }
        // ,{
        //     label: "Error Performance",
        //     pageName: process.env.REACT_APP_ERROR_PERFORMANCE_PAGE,
        //     filters: [
        //         {
        //             $schema: "http://powerbi.com/product/schema#basic",
        //             target: { table: "vw_forecast_error_user_summary", column: "user_details_id" },
        //             operator: "Equals",
        //             values: [userId],
        //             filterType: models.FilterType.BasicFilter
        //         }
        //     ]
        // }
    ];

    useEffect(() => {
        setLoading(true);

        const getPowerBIDetails = async () => {
            try {
                const response = await ApiService.get(`v1/powerBI/embedDetails/group/${groupId}/report/${reportId}`);
                setEmbedConfig({
                    type: 'report',
                    id: response?.data?.reportId,
                    embedUrl: `${response?.data?.embedUrl}`,
                    accessToken: response?.data?.accessToken,
                    tokenType: models.TokenType.Embed,
                    settings: {
                        panes: {
                            filters: {
                                expanded: false,
                                visible: false,
                            },
                        },
                        navContentPaneEnabled: false,
                        layoutType: models.LayoutType.Custom,
                        customLayout: {
                            displayOption: models.DisplayOption.FitToWidth,
                            //// FitToWidth, FitToPage, ActualSize
                        },
                    },
                    filters: [
                        {
                            $schema: "http://powerbi.com/product/schema#basic",
                            target: { table: "vw_forecast_tracker_user_summary", column: "user_details_id" },
                            operator: "Equals",
                            values: [userId],
                            filterType: models.FilterType.BasicFilter
                        }
                    ]
                });
                setLoading(false);
                console.log(userId);

            } catch (error) {
                console.error('API error:', error);
            }
        }
        getPowerBIDetails();

    }, []);

    useEffect(() => {
        if (!embedConfig) return;

        const currentTab = chartTabs[selectedChartTab];

        setEmbedConfig(prev => ({
            ...prev,
            embedUrl: `${prev.embedUrl.split("&pageName=")[0]}&pageName=${currentTab.pageName}`,
            filters: currentTab.filters || []
        }));
    }, [selectedChartTab]);


    return (
        <>
            <Stack>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <div style={{ marginTop: "10px" }}>
                            {/* Chart tabs as pill filter */}
                            <Box sx={{ m: 1, display: "flex", gap: 2 }}>
                                <PillContainer>
                                    <PillHighlight selected={selectedChartTab} />
                                    {chartTabs.map((tab, idx) => (
                                        <PillLabel
                                            key={tab.label}
                                            active={selectedChartTab === idx}
                                            onClick={() => setSelectedChartTab(idx)}
                                            style={{
                                                fontWeight: selectedChartTab === idx ? 600 : 400
                                            }}
                                        >
                                            {tab.label}
                                        </PillLabel>
                                    ))}
                                </PillContainer>
                            </Box>

                            {/* Main content area: Show only the selected graph */}
                            {loading ?
                                <div>
                                    <Box style={{
                                        width: '100%',
                                        height: '40vh',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }} >
                                        <CircularProgress color="inherit" />
                                    </Box>
                                </div>
                                :
                                <Box sx={{ flex: 1, p: 1 }}>
                                    {embedConfig?.embedUrl && embedConfig?.accessToken &&
                                        <PowerBIEmbed
                                            embedConfig={embedConfig}
                                            cssClassName="power-bi-report-height"
                                        />}
                                </Box>
                            }
                        </div>
                    </Grid>
                </Grid>
            </Stack>
        </>
    );
}
export default PowerBIReports;
